import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Calendar,
  Plus,
  Search,
  MapPin,
  Clock,
  Users,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  CheckCircle,
  XCircle,
  Upload,
  Trophy,
  RefreshCw
} from 'lucide-react';
import { eventsService, EventSubmission } from '../services/eventsService';
import { Event, EventCategory, EventMode, EVENT_SPORT_OPTIONS } from '../types/models/event';
import { r2Storage } from '../services/storage/r2Storage';
import AdminWinnerSelector from './AdminWinnerSelector';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import { eventMatchesAssignedRegions, getAssignedRegions, isRegionScopedAdmin, matchesAssignedRegions } from '../utils/regionAccess';
import ExpandableText from './common/ui/ExpandableText';

// Convert "17:30" → "5:30 PM", fixes 24h display confusion
const formatTime = (time: string): string => {
  if (!time) return '';
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = minuteStr || '00';
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minute} ${period}`;
};

// Parse date string safely in local time (avoids UTC midnight = 5:30 AM IST bug)
const parseLocalDate = (dateStr: string | Date): Date => {
  if (dateStr instanceof Date) return dateStr;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T00:00:00');
  }
  return new Date(dateStr);
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    try {
      const converted = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(converted.getTime()) ? null : converted;
    } catch {
      return null;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTimeForInput = (value: any): string => {
  const date = normalizeDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDateTimeForDisplay = (value: any): string => {
  const date = normalizeDate(value);
  return date ? date.toLocaleString() : '';
};


const splitCriteriaLines = (value: string): string[] =>
  value
    .split('\n')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const formatLanguageLabel = (languageCode: string): string => {
  const normalized = languageCode.trim().toLowerCase();
  if (normalized === 'en' || normalized === 'english') return 'English';
  if (normalized === 'hi' || normalized === 'hindi') return 'Hindi';
  return normalized.toUpperCase();
};

const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'hi', label: 'HI', name: 'Hindi' },
  { code: 'mr', label: 'MR', name: 'Marathi' },
  { code: 'ta', label: 'TA', name: 'Tamil' },
  { code: 'te', label: 'TE', name: 'Telugu' },
  { code: 'bn', label: 'BN', name: 'Bengali' },
  { code: 'gu', label: 'GU', name: 'Gujarati' },
  { code: 'pa', label: 'PA', name: 'Punjabi' },
  { code: 'kn', label: 'KN', name: 'Kannada' },
  { code: 'ml', label: 'ML', name: 'Malayalam' },
];

const MultiLangInput: React.FC<{
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  rows?: number;
  placeholder?: string;
  excludeLanguages?: string[];
}> = ({ values, onChange, rows = 3, placeholder = '', excludeLanguages = [] }) => {
  const addLanguage = (code: string) => onChange({ ...values, [code]: '' });
  const removeLanguage = (code: string) => {
    const next = { ...values };
    delete next[code];
    onChange(next);
  };
  const updateLanguage = (code: string, text: string) => onChange({ ...values, [code]: text });

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {SUPPORTED_LANGUAGES.filter((lang) => !excludeLanguages.includes(lang.code)).map((lang) => {
          const isActive = lang.code in values;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => isActive ? removeLanguage(lang.code) : addLanguage(lang.code)}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {lang.label}
            </button>
          );
        })}
      </div>
      {Object.keys(values).length === 0 && (
        <p className="text-xs text-gray-400 italic mb-2">Click a language button above to add content.</p>
      )}
      {Object.entries(values).map(([code, text]) => {
        const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code);
        return (
          <div key={code} className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">{lang?.label || code.toUpperCase()}</span>
                <span className="text-xs text-gray-600">{lang?.name}</span>
              </span>
              <button
                type="button"
                onClick={() => removeLanguage(code)}
                className="text-xs text-red-400 hover:text-red-600"
              >
                ✕ Remove
              </button>
            </div>
            <textarea
              rows={rows}
              value={text}
              onChange={(e) => updateLanguage(code, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={placeholder}
            />
          </div>
        );
      })}
    </div>
  );
};

/** Converts HH:MM (24h) ↔ 12h selectors and emits HH:MM for storage */
const TimeInput12h: React.FC<{
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ value, onChange, required = false }) => {
  const parse = (v: string) => {
    if (!v) return { hour: '', minute: '00', period: 'AM' as 'AM' | 'PM' };
    const [hStr, mStr] = v.split(':');
    const h24 = parseInt(hStr, 10);
    const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return { hour: String(h12), minute: mStr || '00', period };
  };

  const { hour, minute, period } = parse(value);

  const emit = (h: string, m: string, p: string) => {
    if (!h) { onChange(''); return; }
    let h24 = parseInt(h, 10);
    if (p === 'AM') { if (h24 === 12) h24 = 0; }
    else { if (h24 !== 12) h24 += 12; }
    onChange(`${String(h24).padStart(2, '0')}:${m}`);
  };

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  const selectCls = 'px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

  return (
    <div className="flex items-center gap-1">
      <select required={required} value={hour} onChange={(e) => emit(e.target.value, minute, period)} className={selectCls}>
        <option value="">HH</option>
        {hours.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-gray-400 font-bold">:</span>
      <select value={minute} onChange={(e) => emit(hour, e.target.value, period)} className={selectCls}>
        {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select value={period} onChange={(e) => emit(hour, minute, e.target.value)} className={`${selectCls} font-semibold`}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const EventManagement: React.FC = () => {
  const { currentUser, adminProfile } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showWinnerSelector, setShowWinnerSelector] = useState(false);
  const [eventForWinnerSelection, setEventForWinnerSelection] = useState<Event | null>(null);
  const [winnerSelectorSubmissions, setWinnerSelectorSubmissions] = useState<EventSubmission[]>([]);
  const [eventParticipantDetails, setEventParticipantDetails] = useState<EventSubmission[]>([]);
  const [loadingParticipantDetails, setLoadingParticipantDetails] = useState(false);
  const [showWinnerConfirmation, setShowWinnerConfirmation] = useState(false);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [isLive, setIsLive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🎬 ADMIN: Loading events from Supabase...');
      const allEvents = await eventsService.getAllEvents();
      console.log('📊 ADMIN: Events loaded:', allEvents);
      console.log('📈 ADMIN: Total events:', allEvents.length);
      setEvents(allEvents.filter((event) => eventMatchesAssignedRegions(adminProfile, event)));
    } catch (error) {
      console.error('❌ ADMIN: Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [adminProfile]);

  // Debounced refresh for real-time updates
  const debouncedRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    refreshTimeoutRef.current = setTimeout(() => {
      console.log('[EventManagement] Real-time update detected, refreshing...');
      loadEvents();
      setLastUpdate(new Date());
    }, 500);
  }, [loadEvents]);

  // Real-time subscription to events and related tables
  useRealtimeSubscription({
    config: [
      { table: 'events', event: '*' },
      { table: 'event_submissions', event: '*' },
      { table: 'event_participations', event: '*' }
    ],
    onChange: debouncedRefresh,
    enabled: isLive
  });

  useEffect(() => {
    loadEvents();
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [loadEvents]);

  const isEventExpired = (event: Event): boolean => {
    const deadlineDate = normalizeDate(event.submissionDeadline);
    if (deadlineDate) {
      return deadlineDate.getTime() < Date.now();
    }

    const parsedDate = parseLocalDate(event.date);
    if (Number.isNaN(parsedDate.getTime())) return false;

    if (event.time && /^\d{2}:\d{2}$/.test(event.time)) {
      const [hours, minutes] = event.time.split(':').map(Number);
      parsedDate.setHours(hours, minutes, 0, 0);
    }

    return parsedDate.getTime() < Date.now();
  };

  const filteredEvents = events.filter(event => {
    const isCurrentlyActive = event.isActive && !isEventExpired(event);
    if (filterStatus === 'active' && !isCurrentlyActive) return false;
    if (filterStatus === 'inactive' && isCurrentlyActive) return false;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return (
        event.title.toLowerCase().includes(searchLower) ||
        event.description.toLowerCase().includes(searchLower) ||
        event.location.toLowerCase().includes(searchLower) ||
        event.category.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const handleToggleEventStatus = async (event: Event) => {
    try {
      await eventsService.toggleEventStatus(event.id!, !event.isActive);
      await loadEvents();
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Error toggling event status:', error);
    }
  };

  const handleDeleteEvent = async (event: Event) => {
    if (window.confirm(`Are you sure you want to delete "${event.title}"? This action cannot be undone.`)) {
      try {
        await eventsService.deleteEvent(event.id!);
        await loadEvents();
        setActionMenuOpen(null);
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const handleOpenEventDetails = async (event: Event) => {
    try {
      setActionMenuOpen(null);
      setSelectedEvent(event);
      setShowEventDetails(true);
      setLoadingParticipantDetails(true);
      const details = await eventsService.getEventParticipantDetails(event.id!);
      setEventParticipantDetails(details);
    } catch (error) {
      console.error('Error loading participant details:', error);
      setEventParticipantDetails([]);
    } finally {
      setLoadingParticipantDetails(false);
    }
  };

  const handleOpenWinnerSelector = async (event: Event) => {
    try {
      setLoadingSubmissions(true);
      setEventForWinnerSelection(event);
      // Offline events: joining counts → load participants
      // Online events: video submission required → load submissions
      const entries = event.eventMode === 'offline'
        ? await eventsService.getEventParticipants(event.id!)
        : await eventsService.getEventSubmissions(event.id!);
      setWinnerSelectorSubmissions(entries);
      setShowWinnerConfirmation(true);
      setActionMenuOpen(null);
    } catch (error) {
      console.error('Error loading entries:', error);
      alert('Failed to load entries. Please try again.');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleWinnerSelectionSuccess = async (updatedEvent: Event) => {
    await loadEvents();
    setShowWinnerSelector(false);
    setEventForWinnerSelection(null);
    setWinnerSelectorSubmissions([]);
    setShowWinnerConfirmation(false);
    alert('✅ Winners declared successfully!');
  };

  const CreateEventModal: React.FC<{ onClose: () => void; event?: Event }> = ({ onClose, event }) => {
    const [formData, setFormData] = useState({
      title: event?.title || '',
      description: event?.description || '',
      descriptionByLanguage: (() => {
        const langs: Record<string, string> = {};
        if (event?.descriptionByLanguage) {
          Object.entries(event.descriptionByLanguage).forEach(([code, val]) => {
            // Skip 'en' — the primary textarea above handles it
            if (code !== 'en' && val?.trim()) langs[code] = val;
          });
        }
        return langs;
      })(),
      date: event?.date ? (typeof event.date === 'string' ? event.date.split('T')[0] : new Date(event.date).toISOString().split('T')[0]) : '',
      time: event?.time || '',
      location: event?.location || '',
      category: (event?.category || 'other') as string,
      maxParticipants: event?.maxParticipants || 0,
      isActive: event?.isActive ?? true,
      imageUrl: event?.imageUrl || '',
      eventMode: (event?.eventMode || 'online') as EventMode,
      submissionDeadline: event?.submissionDeadline ? formatDateTimeForInput(event.submissionDeadline) : '',
      winnerCount: event?.winnerCount || 3,
      eventRequirements: {
        descriptionByLanguage: (() => {
          const langs: Record<string, string> = {};
          if (event?.eventRequirements?.descriptionByLanguage) {
            Object.entries(event.eventRequirements.descriptionByLanguage).forEach(([code, val]) => {
              if (val?.trim()) langs[code] = val;
            });
          }
          if (!langs['en'] && event?.eventRequirements?.description?.trim()) {
            langs['en'] = event.eventRequirements.description;
          }
          return langs;
        })(),
        criteriaByLanguage: (() => {
          const langs: Record<string, string> = {};
          if (event?.eventRequirements?.criteriaByLanguage) {
            Object.entries(event.eventRequirements.criteriaByLanguage).forEach(([code, arr]) => {
              if (Array.isArray(arr) && arr.length > 0) langs[code] = arr.join('\n');
            });
          }
          if (!langs['en'] && event?.eventRequirements?.criteria?.length) {
            langs['en'] = event.eventRequirements.criteria.join('\n');
          }
          return langs;
        })(),
      },
      metadata: {
        sport: event?.metadata?.sport || '',
        tournamentType: event?.metadata?.tournamentType || '',
        sponsors: event?.metadata?.sponsors || '',
        registrationFee: event?.metadata?.registrationFee || 0,
        judgeName: event?.metadata?.judgeName || '',
        submissionType: event?.metadata?.submissionType || 'video',
        isPublicVoting: event?.metadata?.isPublicVoting || false,
      },
      status: 'upcoming' as const,
      priority: 'medium' as const,
      prizeImages: (event?.prizeImages || []) as string[],
      prizeDescription: event?.prizeDescription || '',
      endDate: event?.endDate || '',
      endTime: event?.endTime || '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [prizeImageFiles, setPrizeImageFiles] = useState<(File | null)[]>([null, null, null]);
    const [prizeImagePreviews, setPrizeImagePreviews] = useState<string[]>(
      event?.prizeImages ? [...event.prizeImages, ...Array(3 - (event.prizeImages.length || 0)).fill('')].slice(0, 3) : ['', '', '']
    );
    const [uploadingPrizeImages, setUploadingPrizeImages] = useState(false);

    const handleMetadataChange = (key: string, value: any) => {
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [key]: value
        }
      }));
    };

    const handlePrizeImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const newFiles = [...prizeImageFiles];
      newFiles[index] = file;
      setPrizeImageFiles(newFiles);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newPreviews = [...prizeImagePreviews];
        newPreviews[index] = ev.target?.result as string;
        setPrizeImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    };

    const removePrizeImage = (index: number) => {
      const newFiles = [...prizeImageFiles];
      newFiles[index] = null;
      setPrizeImageFiles(newFiles);
      const newPreviews = [...prizeImagePreviews];
      newPreviews[index] = '';
      setPrizeImagePreviews(newPreviews);
      const newUrls = [...formData.prizeImages];
      newUrls[index] = '';
      setFormData({ ...formData, prizeImages: newUrls });
    };

    // Handle image file selection
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setImageFile(file);
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    // Upload image to R2 Storage

    const uploadImage = async (file: File): Promise<string> => {
      setUploadingImage(true);
      try {
        console.log('Uploading file:', file.name, 'Size:', file.size);

        // Validate file
        if (!file || file.size === 0) {
          throw new Error('Invalid file selected');
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          throw new Error('File size must be less than 10MB');
        }

        const timestamp = Date.now();
        const fileName = `events/${timestamp}-${file.name}`;

        console.log('Uploading to path:', fileName);
        const result = await r2Storage.uploadFile(fileName, file);
        console.log('Upload result:', result);

        return result.url;
      } catch (error) {
        console.error('Error uploading image:', error);
        setUploadingImage(false); // Reset state on error
        const errorMessage = error instanceof Error ? error.message : String(error);
        throw new Error(`Image upload failed: ${errorMessage}`);
      } finally {
        setUploadingImage(false);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        if (isRegionScopedAdmin(adminProfile) && !matchesAssignedRegions(adminProfile, formData.location)) {
          throw new Error(`Event location must match one of your assigned regions: ${getAssignedRegions(adminProfile).join(', ')}`);
        }

        let finalImageUrl = formData.imageUrl;

        // Upload new image if selected
        if (imageFile) {
          console.log('Starting image upload...');
          finalImageUrl = await uploadImage(imageFile);
          console.log('Image upload completed:', finalImageUrl);
        }

        // Upload prize images (only new files; keep existing URLs for unchanged slots)
        setUploadingPrizeImages(true);
        const finalPrizeImages: string[] = [];
        for (let i = 0; i < 3; i++) {
          if (prizeImageFiles[i]) {
            const url = await uploadImage(prizeImageFiles[i]!);
            finalPrizeImages.push(url);
          } else if (formData.prizeImages[i]) {
            finalPrizeImages.push(formData.prizeImages[i]);
          }
        }
        setUploadingPrizeImages(false);

        const rulesDescByLang: Record<string, string> = {};
        Object.entries(formData.eventRequirements?.descriptionByLanguage || {}).forEach(([code, val]) => {
          if (val?.trim()) rulesDescByLang[code] = val.trim();
        });
        const criteriaByLanguage: Record<string, string[]> = {};
        Object.entries(formData.eventRequirements?.criteriaByLanguage || {}).forEach(([code, val]) => {
          const lines = splitCriteriaLines(val || '');
          if (lines.length > 0) criteriaByLanguage[code] = lines;
        });
        // Combine primary (EN) textarea + any additional language translations
        const mainDescByLang: Record<string, string> = {};
        if (formData.description?.trim()) mainDescByLang['en'] = formData.description.trim();
        Object.entries(formData.descriptionByLanguage || {}).forEach(([code, val]) => {
          if (val?.trim()) mainDescByLang[code] = val.trim();
        });
        const fallbackRequirementsDescription = rulesDescByLang['en'] || Object.values(rulesDescByLang)[0] || '';
        const fallbackCriteriaList = criteriaByLanguage['en'] || Object.values(criteriaByLanguage)[0] || [];

        const eventData = {
          ...formData,
          imageUrl: finalImageUrl,
          category: formData.category as EventCategory,
          eventMode: formData.eventMode,
          submissionDeadline: formData.submissionDeadline ? new Date(formData.submissionDeadline) : undefined,
          winnerCount: formData.winnerCount || 3,
          descriptionByLanguage: Object.keys(mainDescByLang).length > 0 ? mainDescByLang : undefined,
          eventRequirements: (
            fallbackRequirementsDescription ||
            fallbackCriteriaList.length > 0 ||
            Object.keys(rulesDescByLang).length > 0 ||
            Object.keys(criteriaByLanguage).length > 0
          ) ? {
            description: fallbackRequirementsDescription,
            criteria: fallbackCriteriaList,
            ...(Object.keys(rulesDescByLang).length > 0 ? { descriptionByLanguage: rulesDescByLang } : {}),
            ...(Object.keys(criteriaByLanguage).length > 0 ? { criteriaByLanguage } : {})
          } : undefined,
          prizeImages: finalPrizeImages.filter(Boolean),
          prizeDescription: formData.prizeDescription?.trim() || undefined,
          endDate: formData.endDate || undefined,
          endTime: formData.endTime || undefined,
          organizer: 'Admin',
          contactEmail: 'admin@amaplayer.com'
        };

        if (event) {
          await eventsService.updateEvent(event.id!, eventData);
        } else {
          await eventsService.createEvent(eventData);
        }

        await loadEvents();
        // Reset form state
        setImageFile(null);
        setImagePreview('');
        setPrizeImageFiles([null, null, null]);
        setPrizeImagePreviews(['', '', '']);
        onClose();
      } catch (error) {
        console.error('Error saving event:', error);
        console.error('Full error object:', JSON.stringify(error, null, 2));
        let errorMessage = 'Unknown error occurred';

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error !== null) {
          errorMessage = (error as any).code || (error as any).message || JSON.stringify(error);
        } else {
          errorMessage = String(error);
        }

        alert(`Error saving event: ${errorMessage}`);
      } finally {
        setSubmitting(false);
        setUploadingImage(false);
        setUploadingPrizeImages(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {event ? 'Edit Event' : 'Create New Event'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter event title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  English is required. Click a language button to add translations — all are stored as JSON in the <code className="bg-gray-100 px-1 rounded">description</code> column.
                </p>
                <div className="mb-2">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                    <span className="px-1.5 py-0.5 bg-blue-600 text-white rounded text-xs font-bold">EN</span>
                    English <span className="text-red-500">*</span>
                  </span>
                  <textarea
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter event description in English"
                  />
                </div>
                <MultiLangInput
                  values={formData.descriptionByLanguage}
                  onChange={(langs) => setFormData({ ...formData, descriptionByLanguage: langs })}
                  excludeLanguages={['en']}
                  rows={4}
                  placeholder="Enter event description in this language"
                />
              </div>

              {/* Event Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, eventMode: 'online' })}
                    className={`flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                      formData.eventMode === 'online'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl mb-1">🎥</span>
                    <span className="font-semibold text-sm">Online Event</span>
                    <span className="text-xs mt-1 text-center">Users must submit a video to be eligible for winning</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, eventMode: 'offline' })}
                    className={`flex flex-col items-center p-4 border-2 rounded-lg transition-colors ${
                      formData.eventMode === 'offline'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl mb-1">📍</span>
                    <span className="font-semibold text-sm">Offline Event</span>
                    <span className="text-xs mt-1 text-center">Joining the event is enough to be eligible for winning</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time *
                  </label>
                  <TimeInput12h
                    required
                    value={formData.time}
                    onChange={(v) => setFormData({ ...formData, time: v })}
                  />
                </div>
              </div>

              {/* End Date & Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date &amp; Time <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <div className="flex flex-col gap-2">
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <TimeInput12h
                    value={formData.endTime}
                    onChange={(v) => setFormData({ ...formData, endTime: v })}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">When the event ends — leave blank for single-day or open-ended events</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter event location"
                />
                {isRegionScopedAdmin(adminProfile) && (
                  <p className="mt-1 text-xs text-blue-600">
                    Allowed regions: {getAssignedRegions(adminProfile).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Submission Deadline (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.submissionDeadline}
                  onChange={(e) => setFormData({ ...formData, submissionDeadline: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">When users must submit their talent videos</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🏆 Number of Winner Positions (Optional)
                </label>
                <select
                  value={formData.winnerCount || 3}
                  onChange={(e) => setFormData({ ...formData, winnerCount: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">1 Winner (1st Place Only)</option>
                  <option value="2">2 Winners (1st & 2nd Place)</option>
                  <option value="3">3 Winners (Podium - 1st, 2nd, 3rd)</option>
                 
                </select>
                <p className="text-xs text-gray-500 mt-1">How many top submissions to rank as winners. Default is 3 (1st, 2nd, 3rd place).</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Rules Description (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Click a language button to add rules text for that language. Stored as multilingual JSON.
                </p>
                <MultiLangInput
                  values={formData.eventRequirements.descriptionByLanguage}
                  onChange={(langs) => setFormData({
                    ...formData,
                    eventRequirements: { ...formData.eventRequirements, descriptionByLanguage: langs }
                  })}
                  rows={2}
                  placeholder="e.g., Important information about the event rules"
                />
              </div>

           

              {/* Event Image Upload */}
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📸 Event Image (Optional)
                </label>

                {/* Current Image Preview */}
                {(formData.imageUrl || imagePreview) && (
                  <div className="mb-3 relative">
                    <img
                      src={imagePreview || formData.imageUrl}
                      alt="Event preview"
                      className="w-full h-32 object-cover rounded border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview('');
                        setFormData({ ...formData, imageUrl: '' });
                      }}
                      className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow"
                      title="Remove image"
                    >
                      ✕
                    </button>
                    <p className="text-xs text-green-600 mt-1">
                      {imagePreview ? '✨ New image ready to upload' : '✅ Current event image'}
                    </p>
                  </div>
                )}

                {/* File Input */}
                <div className="flex items-center justify-center w-full">
                  <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded cursor-pointer transition-colors ${uploadingImage
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                    }`}>
                    <div className="flex flex-col items-center justify-center py-2">
                      {uploadingImage ? (
                        <>
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-1"></div>
                          <p className="text-xs text-blue-600">Uploading...</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 mb-1 text-gray-400" />
                          <p className="text-xs text-gray-500">
                            <span className="font-semibold">Click</span> to upload image
                          </p>
                          <p className="text-xs text-gray-400">PNG, JPG, GIF (up to 10MB)</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              </div>

              {/* Prize Section */}
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🏆 Prize Description (Optional)
                </label>
                <textarea
                  rows={3}
                  value={formData.prizeDescription}
                  onChange={(e) => setFormData({ ...formData, prizeDescription: e.target.value })}
                  placeholder="e.g. 1st place: ₹5000 cash + trophy, 2nd place: ₹2000 + medal, 3rd place: ₹1000"
                  className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm resize-none"
                />
              </div>

              {/* Prize Images Upload */}
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🏆 Prize Images (Optional, max 3)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex flex-col items-center">
                      <span className="text-xs text-gray-500 mb-1">
                        {i === 0 ? '🥇 1st' : i === 1 ? '🥈 2nd' : '🥉 3rd'}
                      </span>
                      {prizeImagePreviews[i] || formData.prizeImages[i] ? (
                        <div className="relative w-full">
                          <img
                            src={prizeImagePreviews[i] || formData.prizeImages[i]}
                            alt={`Prize ${i + 1}`}
                            className="w-full h-24 object-cover rounded border border-yellow-300"
                          />
                          <button
                            type="button"
                            onClick={() => removePrizeImage(i)}
                            className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded cursor-pointer transition-colors ${
                          uploadingPrizeImages ? 'border-yellow-400 bg-yellow-100' : 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                        }`}>
                          {uploadingPrizeImages ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600" />
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-yellow-400 mb-1" />
                              <span className="text-xs text-gray-500">Upload</span>
                            </>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => handlePrizeImageChange(i, e)}
                            disabled={uploadingPrizeImages || submitting}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as Event['category'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category</option>
                    <option value="tournament">Tournament</option>
                    <option value="training">Training</option>
                    <option value="workshop">Workshop</option>
                    <option value="competition">Competition</option>
                    <option value="meetup">Meetup</option>
                    <option value="seminar">Seminar</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxParticipants}
                    onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0 for unlimited"
                  />
                </div>
              </div>

              {/* Category-Specific Fields */}
              {formData.category === 'tournament' && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 space-y-4">
                  <h3 className="font-semibold text-blue-900 border-b border-blue-200 pb-2">Tournament Specifics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sport</label>
                      <select
                        value={formData.metadata.sport || ''}
                        onChange={(e) => handleMetadataChange('sport', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select sport</option>
                        {EVENT_SPORT_OPTIONS.map((sport) => (
                          <option key={sport.value} value={sport.value}>
                            {sport.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tournament Type</label>
                      <select
                        value={formData.metadata.tournamentType || ''}
                        onChange={(e) => handleMetadataChange('tournamentType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select type</option>
                        <option value="knockout">Single Elimination (Knockout)</option>
                        <option value="league">League (Round Robin)</option>
                        <option value="group_knockout">Group + Knockout</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Registration Fee</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">₹</span>
                        <input
                          type="number"
                          min="0"
                          value={formData.metadata.registrationFee || 0}
                          onChange={(e) => handleMetadataChange('registrationFee', parseInt(e.target.value) || 0)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-sm font-medium text-gray-700 mb-2">Sponsors</label>
                       <textarea
                         rows={2}
                         value={formData.metadata.sponsors || ''}
                         onChange={(e) => handleMetadataChange('sponsors', e.target.value)}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         placeholder="List sponsors (e.g., Nike, Red Bull)"
                       />
                    </div>
                  </div>
                </div>
              )}

              {formData.category === 'competition' && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 space-y-4">
                  <h3 className="font-semibold text-purple-900 border-b border-purple-200 pb-2">Competition Specifics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Judge Name</label>
                      <input
                        type="text"
                        value={formData.metadata.judgeName || ''}
                        onChange={(e) => handleMetadataChange('judgeName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Submission Type</label>
                      <select
                        value={formData.metadata.submissionType || 'video'}
                        onChange={(e) => handleMetadataChange('submissionType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="video">Video Required</option>
                        <option value="photo">Photo / Picture</option>
                        <option value="text">Text / Essay</option>
                      </select>
                    </div>
                    <div className="md:col-span-2 flex items-center">
                      <input
                        type="checkbox"
                        id="isPublicVoting"
                        checked={formData.metadata.isPublicVoting || false}
                        onChange={(e) => handleMetadataChange('isPublicVoting', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isPublicVoting" className="ml-2 block text-sm text-gray-900">
                        Enable Public Voting (Community can vote for submissions)
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Event is active and visible to users
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  disabled={submitting || uploadingImage || uploadingPrizeImages}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || uploadingImage || uploadingPrizeImages}
                  className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors duration-200 flex items-center space-x-2"
                >
                  {submitting || uploadingImage || uploadingPrizeImages ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>
                        {uploadingImage ? 'Uploading Image...' : uploadingPrizeImages ? 'Uploading Prize Images...' : 'Saving Event...'}
                      </span>
                    </>
                  ) : (
                    <span>{event ? '✏️ Update Event' : '➕ Create Event'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const EventDetailsModal: React.FC<{
    event: Event;
    onClose: () => void;
    participantDetails: EventSubmission[];
    participantLoading: boolean;
  }> = ({ event, onClose, participantDetails, participantLoading }) => (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold text-gray-900">Event Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Close"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.title}</h3>
              <div className="flex items-center space-x-2 mb-4">
                {event.status === 'completed' || (event.leaderboard && event.leaderboard.length > 0) ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    🏆 Completed
                  </span>
                ) : (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    isEventExpired(event)
                      ? 'bg-orange-100 text-orange-800'
                      : event.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                  }`}>
                    {isEventExpired(event) ? (
                      <>
                        <Clock className="w-3 h-3 mr-1" />
                        Expired
                      </>
                    ) : event.isActive ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactive
                      </>
                    )}
                  </span>
                )}
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  event.eventMode === 'offline'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-indigo-100 text-indigo-800'
                }`}>
                  {event.eventMode === 'offline' ? '📍 Offline Event' : '🎥 Online Event'}
                </span>
              </div>
            </div>

            {(event.prizeDescription || (event.prizeImages && event.prizeImages.length > 0)) && (
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">🏆 Prizes</label>
                {event.prizeDescription && (
                  <ExpandableText text={event.prizeDescription} className="text-sm text-gray-800 mb-3" />
                )}
                {event.prizeImages && event.prizeImages.length > 0 && (
                  <div className="flex gap-3">
                    {event.prizeImages.map((url, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <img
                          src={url}
                          alt={`Prize ${i + 1}`}
                          className="w-24 h-24 object-cover rounded-lg border border-yellow-300 shadow-sm"
                        />
                        <span className="text-xs text-gray-500">
                          {i === 0 ? '🥇 1st' : i === 1 ? '🥈 2nd' : '🥉 3rd'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

                    {event.descriptionByLanguage &&
                    Object.entries(event.descriptionByLanguage).some(([, value]) => value?.trim()) && (
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Event Description (By Language)</label>
                    <div className="mt-2 space-y-2">
                    {Object.entries(event.descriptionByLanguage)
                    .filter(([, value]) => Boolean(value?.trim()))
                    .map(([languageCode, value]) => (
                      <div key={languageCode} className="rounded border border-gray-200 p-2">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          {formatLanguageLabel(languageCode)}
                        </p>
                        <ExpandableText text={value} limit={200} className="text-sm text-gray-900 mt-1" />
                      </div>
                    ))}
                    </div>
                    </div>
                    )}
            <div>
               <label className="block text-sm font-medium text-gray-700">Winner Count</label>
              <p className="mt-1 text-sm text-gray-900">{event.winnerCount}</p>
            </div>
            {event.organizer && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Organizer</label>
                <p className="mt-1 text-sm text-gray-900">{event.organizer}</p>
              </div>
            )}
             {event.metadata.sponsors && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Sponsors</label>
                <p className="mt-1 text-sm text-gray-900">{event.metadata.sponsors}</p>
              </div>
            )}
             {event.metadata.registrationFee && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration Fee</label>
                <p className="mt-1 text-sm text-gray-900">{event.metadata.registrationFee}</p>
              </div>
            )}
           {event.metadata.tournamentType && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Tournament Type</label>
                <p className="mt-1 text-sm text-gray-900">{event.metadata.tournamentType}</p>
              </div>
            )}
            {event.metadata.sport && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Sport</label>
                <p className="mt-1 text-sm text-gray-900">{event.metadata.sport}</p>
              </div>
            )}
       

            {event.eventRequirements?.descriptionByLanguage &&
              Object.entries(event.eventRequirements.descriptionByLanguage).some(([, value]) => value?.trim()) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Event Requirements Description (By Language)</label>
                <div className="mt-2 space-y-2">
                  {Object.entries(event.eventRequirements.descriptionByLanguage)
                    .filter(([, value]) => Boolean(value?.trim()))
                    .map(([languageCode, value]) => (
                      <div key={languageCode} className="rounded border border-gray-200 p-2">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          {formatLanguageLabel(languageCode)}
                        </p>
                        <ExpandableText text={value} limit={200} className="text-sm text-gray-900 mt-1" />
                      </div>
                    ))}
                </div>
              </div>
            )}

           

         
            <div className={`p-3 rounded-lg border text-sm ${
              event.eventMode === 'offline'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
              {event.eventMode === 'offline'
                ? '📍 Offline Event — Participants who join this event are eligible for winner selection.'
                : '🎥 Online Event — Participants must submit a video to be eligible for winner selection.'}
            </div>

            {event.leaderboard && event.leaderboard.length > 0 && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="text-md font-bold text-yellow-800 mb-3 flex items-center">
                  <Trophy className="w-4 h-4 mr-2" />
                  Winners
                </h4>
                <div className="space-y-3">
                  {event.leaderboard.sort((a, b) => a.rank - b.rank).map((winner) => (
                    <div key={winner.rank} className="flex items-center justify-between bg-white p-2 rounded shadow-sm">
                      <div className="flex items-center gap-3">
                        <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
                          winner.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                          winner.rank === 2 ? 'bg-gray-100 text-gray-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          #{winner.rank}
                        </span>
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {winner.userAvatar ? (
                            <img src={winner.userAvatar} alt={winner.userName} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-gray-500 text-xs font-semibold">
                              {(winner.userName || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <a
                            href={`/users?search=${encodeURIComponent(winner.userName || winner.userId)}`}
                            className="font-medium text-blue-700 hover:text-blue-900 hover:underline text-sm"
                          >
                            {winner.userName || 'Unknown User'}
                          </a>
                          <p
                            className="text-xs text-gray-400 font-mono cursor-pointer hover:text-gray-600"
                            title="Click to copy user ID"
                            onClick={() => navigator.clipboard.writeText(winner.userId)}
                          >
                            {winner.userId}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">{winner.prize || 'No Prize'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date & Time</label>
                <div className="mt-1 flex items-center text-sm text-gray-900">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="font-medium mr-1">Start:</span>
                  {parseLocalDate(event.date).toLocaleDateString()} at {formatTime(event.time || '')}
                </div>
                {(event.endDate || event.endTime) && (
                  <div className="mt-1 flex items-center text-sm text-gray-900">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="font-medium mr-1">End:</span>
                    {event.endDate ? parseLocalDate(event.endDate).toLocaleDateString() : '—'}
                    {event.endTime ? ` at ${formatTime(event.endTime)}` : ''}
                  </div>
                )}
                {event.submissionDeadline && (
                  <div className="mt-1 flex items-center text-sm text-gray-900">
                    <Clock className="w-4 h-4 mr-2 text-gray-400" />
                    Submission deadline: {formatDateTimeForDisplay(event.submissionDeadline)}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <div className="mt-1 flex items-center text-sm text-gray-900">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  {event.location}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{event.participants?.length || 0}</p>
                <p className="text-sm text-gray-600">Participants</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">
                  {event.maxParticipants || '∞'}
                </p>
                <p className="text-sm text-gray-600">Max Capacity</p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Participants Details & Uploaded Videos</h4>
              {participantLoading ? (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">
                  Loading participant details...
                </div>
              ) : participantDetails.length === 0 ? (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-600">
                  No participant records found for this event yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {participantDetails.map((participant) => (
                    <div
                      key={`${participant.userId}-${participant.id}`}
                      className="border border-gray-200 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                            {participant.userAvatar ? (
                              <img
                                src={participant.userAvatar}
                                alt={participant.userName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gray-500 text-sm font-semibold">
                                {(participant.userName || 'U').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{participant.userName || 'Unknown User'}</p>
                            <p className="text-xs text-gray-600">{participant.email || 'Email not available'}</p>
                            <p className="text-xs text-gray-500 font-mono">{participant.userId}</p>
                          </div>
                        </div>
                        {participant.role && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                            {participant.role}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Location:</span>{' '}
                          <span className="text-gray-900">{participant.location || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Mobile:</span>{' '}
                          <span className="text-gray-900">{participant.mobile || participant.phone || 'Not provided'}</span>
                        </div>
                        <div className="md:col-span-2">
                          <span className="text-gray-500">Sports:</span>{' '}
                          <span className="text-gray-900">
                            {participant.sports && participant.sports.length > 0 ? participant.sports.join(', ') : 'Not specified'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Followers:</span>{' '}
                          <span className="text-gray-900">{participant.followersCount ?? 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Following:</span>{' '}
                          <span className="text-gray-900">{participant.followingCount ?? 0}</span>
                        </div>
                        {participant.bio && (
                          <div className="md:col-span-2">
                            <span className="text-gray-500">Bio:</span>{' '}
                            <span className="text-gray-900">{participant.bio}</span>
                          </div>
                        )}
                      </div>

                      {participant.videoUrl ? (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Uploaded Video</p>
                          <video
                            src={participant.videoUrl}
                            controls
                            className="w-full rounded-lg border border-gray-200 max-h-64 bg-black"
                          />
                          {(participant.title || participant.description) && (
                            <div className="mt-2 text-xs text-gray-600 space-y-1">
                              {participant.title && <p><span className="font-medium">Title:</span> {participant.title}</p>}
                              {participant.description && <p><span className="font-medium">Description:</span> {participant.description}</p>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                          No uploaded video found for this participant.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {event.createdAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Created</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(typeof event.createdAt === 'string' ? event.createdAt : event.createdAt instanceof Date ? event.createdAt : (event.createdAt && 'toDate' in event.createdAt) ? event.createdAt.toDate() : new Date()).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Event Management</h1>
            <p className="text-gray-600 mt-1">
              {isRegionScopedAdmin(adminProfile)
                ? `Create and manage events in ${getAssignedRegions(adminProfile).join(', ') || 'your assigned regions'}`
                : 'Create and manage events for the mobile app'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Live Status */}
            <button
              onClick={() => setIsLive(!isLive)}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isLive
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span>{isLive ? 'Live' : 'Paused'}</span>
            </button>
            {lastUpdate && (
              <span className="text-xs text-gray-500">
                Updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            {/* Manual Refresh */}
            <button
              onClick={() => {
                loadEvents();
                setLastUpdate(new Date());
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            {/* Create Event */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>Create Event</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      {isRegionScopedAdmin(adminProfile) && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Region scope active: you can view and manage events only in {getAssignedRegions(adminProfile).join(', ') || 'your assigned regions'}.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search events by title, description, or location..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Events</option>
              <option value="active">Active Events</option>
              <option value="inactive">Inactive Events</option>
            </select>
          </div>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.title}</h3>
                  <div className="flex items-center space-x-2 flex-wrap">
                    {event.status === 'completed' || (event.leaderboard && event.leaderboard.length > 0) ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        🏆 Completed
                      </span>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isEventExpired(event)
                          ? 'bg-orange-100 text-orange-800'
                          : event.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isEventExpired(event) ? 'Expired' : event.isActive ? 'Active' : 'Inactive'}
                      </span>
                    )}
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                    </span>
                    {event.leaderboard && event.leaderboard.length > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Winners Declared
                      </span>
                    )}
                    {(!event.leaderboard || event.leaderboard.length === 0) && event.submissionDeadline && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        ⏳ Pending Winners
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      event.eventMode === 'offline'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {event.eventMode === 'offline' ? '📍 Offline' : '🎥 Online'}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setActionMenuOpen(actionMenuOpen === event.id ? null : event.id!)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {actionMenuOpen === event.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleOpenEventDetails(event)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Eye className="w-4 h-4 inline mr-2" />
                          View Details
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowCreateModal(true);
                            setActionMenuOpen(null);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Edit className="w-4 h-4 inline mr-2" />
                          Edit Event
                        </button>
                        <button
                          onClick={() => handleToggleEventStatus(event)}
                          disabled={isEventExpired(event)}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            isEventExpired(event)
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {isEventExpired(event) ? (
                            <>
                              <Clock className="w-4 h-4 inline mr-2" />
                              Expired
                            </>
                          ) : event.isActive ? (
                            <>
                              <XCircle className="w-4 h-4 inline mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 inline mr-2" />
                              Activate
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleOpenWinnerSelector(event)}
                          disabled={loadingSubmissions || event.leaderboard?.length > 0}
                          className={`block w-full text-left px-4 py-2 text-sm ${event.leaderboard?.length > 0
                            ? 'text-green-700'
                            : 'text-orange-700 hover:bg-gray-100'
                            } ${event.leaderboard?.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Trophy className="w-4 h-4 inline mr-2" />
                          {event.leaderboard?.length > 0 ? '✓ Winners Declared' : 'Declare Winners'}
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event)}
                          className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                        >
                          <Trash2 className="w-4 h-4 inline mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <ExpandableText text={event.description} limit={100} className="text-gray-600 text-sm mb-4" />

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {parseLocalDate(event.date).toLocaleDateString()} at {formatTime(event.time || '')}
                </div>
                {event.submissionDeadline && (
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Submission deadline: {formatDateTimeForDisplay(event.submissionDeadline)}
                  </div>
                )}
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-2" />
                  {event.location}
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  {event.participants?.length || 0} participants
                  {Number(event.maxParticipants) > 0 && ` / ${event.maxParticipants} max`}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first event.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Event
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          event={selectedEvent || undefined}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          participantDetails={eventParticipantDetails}
          participantLoading={loadingParticipantDetails}
          onClose={() => {
            setShowEventDetails(false);
            setSelectedEvent(null);
            setEventParticipantDetails([]);
            setLoadingParticipantDetails(false);
          }}
        />
      )}

      {/* Winner Confirmation Dialog */}
      {showWinnerConfirmation && eventForWinnerSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Declare Winners?</h3>
            <p className="text-gray-600 mb-4">
              Declare winners for <strong>{eventForWinnerSelection.title}</strong>?
            </p>
            {winnerSelectorSubmissions.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ This event has no {eventForWinnerSelection.eventMode === 'offline' ? 'participants' : 'submissions'} yet. Winners cannot be declared.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                Found {winnerSelectorSubmissions.length}{' '}
                {eventForWinnerSelection.eventMode === 'offline' ? 'participant' : 'submission'}{winnerSelectorSubmissions.length !== 1 ? 's' : ''}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowWinnerConfirmation(false);
                  setEventForWinnerSelection(null);
                  setWinnerSelectorSubmissions([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                disabled={loadingSubmissions}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowWinnerSelector(true);
                  setShowWinnerConfirmation(false);
                }}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                disabled={loadingSubmissions || winnerSelectorSubmissions.length === 0}
              >
                {loadingSubmissions ? 'Loading...' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Winner Selector Modal */}
      {showWinnerSelector && eventForWinnerSelection && currentUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8">
            <AdminWinnerSelector
              event={eventForWinnerSelection}
              submissions={winnerSelectorSubmissions}
              onSuccess={handleWinnerSelectionSuccess}
              onCancel={() => {
                setShowWinnerSelector(false);
                setEventForWinnerSelection(null);
                setWinnerSelectorSubmissions([]);
              }}
              adminId={currentUser.uid}
              isOfflineEvent={eventForWinnerSelection.eventMode === 'offline'}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
